import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, Loader2, Pencil, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Profile {
  user_id: string;
  full_name: string;
  email: string | null;
  plan: string | null;
  payment_status: string | null;
  created_at: string | null;
}

interface UsageRow {
  user_id: string;
  count: number;
}

export default function AdminUsuarios() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [usage, setUsage] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    full_name: "",
    plan: "free",
    payment_status: "inactive",
  });

  const [editing, setEditing] = useState<Profile | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editPassword, setEditPassword] = useState("");
  const [deleting, setDeleting] = useState<Profile | null>(null);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id || null));
  }, []);

  async function load() {
    setLoading(true);
    const { data: profs } = await supabase
      .from("profile")
      .select("user_id, full_name, email, plan, payment_status, created_at")
      .order("created_at", { ascending: false });
    setProfiles(profs || []);

    const month = new Date().toISOString().slice(0, 7);
    const { data: usageRows } = await supabase
      .from("simulation_usage")
      .select("user_id, count")
      .eq("month", month);
    const map: Record<string, number> = {};
    (usageRows as UsageRow[] | null)?.forEach((r) => {
      if (r.user_id) map[r.user_id] = r.count;
    });
    setUsage(map);

    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const filtered = profiles.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.full_name.toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.plan || "").toLowerCase().includes(q)
    );
  });

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString("pt-BR") : "—";

  async function invokeAdmin(body: Record<string, unknown>) {
    const { data, error } = await supabase.functions.invoke("admin-manage-user", {
      body,
    });
    if (error) throw error;
    if ((data as any)?.error) throw new Error((data as any).error);
    return data;
  }

  async function handleCreate() {
    if (!newUser.email || !newUser.password) {
      toast.error("Email e senha são obrigatórios");
      return;
    }
    setCreating(true);
    try {
      await invokeAdmin({ action: "create", ...newUser });
      toast.success("Usuário criado!");
      setCreateOpen(false);
      setNewUser({ email: "", password: "", full_name: "", plan: "free", payment_status: "inactive" });
      load();
    } catch (e: any) {
      toast.error(e.message || "Erro ao criar usuário");
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveEdit() {
    if (!editing) return;
    setSavingEdit(true);
    try {
      await invokeAdmin({
        action: "update_user",
        user_id: editing.user_id,
        email: editing.email,
        full_name: editing.full_name,
        password: editPassword || undefined,
        plan: editing.plan,
        payment_status: editing.payment_status,
      });
      toast.success("Usuário atualizado!");
      setEditing(null);
      setEditPassword("");
      load();
    } catch (e: any) {
      toast.error(e.message || "Erro ao atualizar");
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleResetUsage(p: Profile) {
    if (!confirm(`Zerar contador de simulações deste mês para ${p.email}?`)) return;
    try {
      await invokeAdmin({ action: "reset_usage", user_id: p.user_id });
      toast.success("Contador zerado!");
      load();
    } catch (e: any) {
      toast.error(e.message || "Erro ao zerar contador");
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    setDeletingLoading(true);
    try {
      await invokeAdmin({ action: "delete", user_id: deleting.user_id });
      toast.success("Usuário excluído!");
      setProfiles((prev) => prev.filter((x) => x.user_id !== deleting.user_id));
      setDeleting(null);
    } catch (e: any) {
      toast.error(e.message || "Erro ao excluir usuário");
    } finally {
      setDeletingLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-heading font-bold">Usuários</h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Novo usuário
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome, email ou plano..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <p className="text-muted-foreground text-center py-12">Carregando...</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead>Uso (mês)</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nenhum usuário encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((p) => (
                      <TableRow key={p.user_id}>
                        <TableCell className="font-medium">{p.full_name}</TableCell>
                        <TableCell className="text-sm">{p.email || "—"}</TableCell>
                        <TableCell>
                          <Badge variant={p.plan === "pro" ? "default" : "secondary"}>
                            {p.plan || "free"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              p.payment_status === "active" || p.payment_status === "paid"
                                ? "default"
                                : "secondary"
                            }
                          >
                            {p.payment_status || "inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {usage[p.user_id] ?? 0}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(p.created_at)}</TableCell>
                        <TableCell className="text-right space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleResetUsage(p)}>
                            <RefreshCcw className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            disabled={p.user_id === currentUserId}
                            title={p.user_id === currentUserId ? "Você não pode excluir a si mesmo" : "Excluir usuário"}
                            onClick={() => setDeleting(p)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo usuário</DialogTitle>
            <DialogDescription>
              Cria a conta no Auth do Supabase e o perfil já vinculado ao plano selecionado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome completo</Label>
              <Input
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                placeholder="João Silva"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="usuario@email.com"
              />
            </div>
            <div>
              <Label>Senha temporária</Label>
              <Input
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plano</Label>
                <Select
                  value={newUser.plan}
                  onValueChange={(v) => setNewUser({ ...newUser, plan: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">PRO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status pagamento</Label>
                <Select
                  value={newUser.payment_status}
                  onValueChange={(v) => setNewUser({ ...newUser, payment_status: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inactive">Inativo</SelectItem>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar usuário"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditPassword(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
            <DialogDescription>
              Atualize dados de acesso, plano e pagamento.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Nome completo</Label>
                <Input
                  value={editing.full_name || ""}
                  onChange={(e) => setEditing({ ...editing, full_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editing.email || ""}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Nova senha (opcional)</Label>
                <Input
                  type="text"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  placeholder="Deixe em branco para não alterar"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plano</Label>
                <Select
                  value={editing.plan || "free"}
                  onValueChange={(v) => setEditing({ ...editing, plan: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">PRO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status pagamento</Label>
                <Select
                  value={editing.payment_status || "inactive"}
                  onValueChange={(v) => setEditing({ ...editing, payment_status: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inactive">Inativo</SelectItem>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setEditPassword(""); }}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. O usuário <strong>{deleting?.email || deleting?.full_name}</strong> será removido do Auth e todos os seus dados de perfil, contadores e papéis serão apagados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deletingLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
